import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DownloadSimple, ArrowSquareOut, Receipt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  paid: { label: "Pagata", cls: "bg-green-50 text-green-700 border-green-200" },
  open: { label: "In sospeso", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  draft: { label: "Bozza", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  uncollectible: { label: "Non incassata", cls: "bg-red-50 text-red-700 border-red-200" },
  void: { label: "Annullata", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const formatEUR = (cents, currency = "EUR") =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency }).format((cents || 0) / 100);
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

export default function BillingHistory() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [year, setYear] = useState("all");
  const [portalLoading, setPortalLoading] = useState(false);

  const load = async (y) => {
    setLoading(true);
    try {
      const params = (y && y !== "all") ? { year: y } : {};
      const r = await axios.get(`${API}/billing/history`, { params, withCredentials: true });
      setData(r.data);
    } catch {
      setData({ invoices: [], stripe_available: false });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(year); }, [year]);

  const invoices = data?.invoices || [];
  const years = useMemo(() => {
    const s = new Set();
    invoices.forEach(i => { if (i.date) s.add(i.date.slice(0, 4)); });
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [invoices]);

  const downloadInvoice = async (inv) => {
    try {
      const r = await axios.get(`${API}/billing/invoice/${inv.id}/pdf`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const ym = inv.date ? inv.date.slice(0, 7) : "invoice";
      a.download = `Fattura_Selly_${ym}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore download fattura");
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await axios.post(`${API}/billing/portal-session`, {
        return_url: window.location.origin + "/settings?tab=fatturazione",
      }, { withCredentials: true });
      if (r.data?.url) window.open(r.data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portal Stripe non disponibile");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="billing-history">
      {/* Next invoice / manage subscription */}
      {data?.next_invoice && (
        <Card className="border-[#1B4FD8]/20 bg-[#1B4FD8]/[0.03]" data-testid="next-invoice-card">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#1B4FD8]">Prossimo addebito</div>
              <div className="text-sm text-[#0D1F3C] mt-1">
                Rinnovo il <strong>{formatDate(data.next_invoice.date)}</strong>
                &nbsp;· Importo stimato: <strong>{formatEUR(data.next_invoice.amount_cents, data.next_invoice.currency)}</strong>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="manage-subscription-btn"
              className="border-[#1B4FD8]/30 text-[#1B4FD8] hover:bg-[#1B4FD8]/5"
            >
              <ArrowSquareOut size={14} className="mr-1.5" />
              {portalLoading ? "Apro…" : "Gestisci abbonamento"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#0D1F3C]/8">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt size={18} className="text-[#1B4FD8]" weight="duotone" />
            Storico Fatturazione
          </CardTitle>
          {years.length > 1 && (
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </CardHeader>
        <CardContent>
          {loading && <div className="py-10 text-center text-sm text-[#0D1F3C]/50" data-testid="billing-loading">Caricamento…</div>}

          {!loading && !data?.stripe_available && (
            <div className="py-8 text-center" data-testid="billing-unavailable">
              <p className="text-sm text-[#0D1F3C]/70">{data?.notice || "Storico fatture non disponibile."}</p>
              <p className="text-xs text-[#0D1F3C]/50 mt-2">
                Le fatture saranno visibili dopo il primo pagamento con integrazione Stripe live.
              </p>
            </div>
          )}

          {!loading && data?.stripe_available && invoices.length === 0 && (
            <div className="py-10 text-center" data-testid="billing-empty">
              <p className="text-sm text-[#0D1F3C]/70">Nessuna fattura ancora.</p>
              <Button
                size="sm"
                onClick={() => window.location.href = "/pricing"}
                className="mt-3 bg-[#1B4FD8] hover:bg-[#0D1F3C]"
                data-testid="goto-pricing-from-empty"
              >
                Inizia il tuo primo piano
              </Button>
            </div>
          )}

          {!loading && invoices.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="invoices-table">
                  <thead>
                    <tr className="border-b border-[#0D1F3C]/10 text-[#0D1F3C]/60">
                      <th className="text-left font-medium py-2 px-2">Data</th>
                      <th className="text-left font-medium py-2 px-2">Descrizione</th>
                      <th className="text-right font-medium py-2 px-2">Importo</th>
                      <th className="text-center font-medium py-2 px-2">Stato</th>
                      <th className="text-right font-medium py-2 px-2">Azione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => {
                      const st = STATUS_MAP[inv.status] || { label: inv.status, cls: "bg-slate-50 text-slate-600 border-slate-200" };
                      return (
                        <tr key={inv.id} className="border-b border-[#0D1F3C]/5 hover:bg-[#1B4FD8]/[0.02]" data-testid={`invoice-row-${inv.id}`}>
                          <td className="py-3 px-2 text-[#0D1F3C]/80">{formatDate(inv.date)}</td>
                          <td className="py-3 px-2 text-[#0D1F3C] truncate max-w-[300px]">{inv.description || "—"}</td>
                          <td className="py-3 px-2 text-right font-medium text-[#0D1F3C]">{formatEUR(inv.amount_cents, inv.currency)}</td>
                          <td className="py-3 px-2 text-center">
                            <Badge variant="outline" className={`text-[11px] font-medium ${st.cls}`} data-testid={`invoice-status-${inv.id}`}>{st.label}</Badge>
                          </td>
                          <td className="py-3 px-2 text-right">
                            {inv.status === "paid" && inv.invoice_pdf_url ? (
                              <Button size="sm" variant="outline" onClick={() => downloadInvoice(inv)} data-testid={`invoice-download-${inv.id}`} className="h-7 px-2 text-[11px]">
                                <DownloadSimple size={12} className="mr-1" />PDF
                              </Button>
                            ) : inv.hosted_invoice_url ? (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#1B4FD8] hover:underline">Apri ↗</a>
                            ) : <span className="text-[11px] text-[#0D1F3C]/30">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2" data-testid="invoices-cards">
                {invoices.map(inv => {
                  const st = STATUS_MAP[inv.status] || { label: inv.status, cls: "bg-slate-50 text-slate-600 border-slate-200" };
                  return (
                    <div key={inv.id} className="rounded-lg border border-[#0D1F3C]/8 p-3 flex flex-col gap-1.5" data-testid={`invoice-card-${inv.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-[#0D1F3C]/60">{formatDate(inv.date)}</span>
                        <Badge variant="outline" className={`text-[10px] font-medium ${st.cls}`}>{st.label}</Badge>
                      </div>
                      <div className="text-sm text-[#0D1F3C] font-medium">{inv.description}</div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-base font-semibold text-[#0D1F3C]">{formatEUR(inv.amount_cents, inv.currency)}</span>
                        {inv.status === "paid" && inv.invoice_pdf_url ? (
                          <Button size="sm" variant="outline" onClick={() => downloadInvoice(inv)} className="h-7 px-2 text-[11px]">
                            <DownloadSimple size={12} className="mr-1" />PDF
                          </Button>
                        ) : inv.hosted_invoice_url ? (
                          <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#1B4FD8] hover:underline">Apri ↗</a>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
